import { useEffect } from 'react';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, buttonVariants, cn, Wordmark } from '@wundo/ui';
import { m } from '@/paraglide/messages.js';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import {
  PILOT_CONTEXT_KEY,
  ensurePilotContext,
  fetchPilotContext,
} from '@wundo/client/pilot.ts';
import { applyUserLocale } from '@/lib/locale.ts';
import { currentUserOrNull } from '@/lib/pilotEnded.ts';

/*
 * Where a pilot that has ended leaves the person holding it. Outside `_app`
 * like the unsubscribe page, and for the same reason: nothing is left behind
 * the rail to navigate to, and a shell of empty pages only invites a hunt for
 * what is gone. The one thing still wanted here is a reply.
 */
export const Route = createFileRoute('/pilot-ended')({
  /*
   * The pilot is fetched here because the first line is written from it: a
   * paragraph that named no length and grew one a moment later would have
   * rewritten itself under the reader.
   */
  beforeLoad: async ({ context }) => {
    const user = await currentUserOrNull(context.queryClient);

    /*
     * Anyone still in a pilot — and anyone with no session to read — goes to
     * the app, which has its own guard for whichever of the two they are.
     */
    if (user?.status !== 'expired') {
      throw redirect({ to: '/', replace: true });
    }

    await ensurePilotContext(context.queryClient);
  },
  component: PilotEndedPage,
});

export function PilotEndedPage() {
  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const { data: pilot } = useQuery({
    queryKey: PILOT_CONTEXT_KEY,
    queryFn: fetchPilotContext,
  });

  /* Signed in, so the browser's language stops being the best guess we have. */
  useEffect(() => {
    applyUserLocale(user?.language);
  }, [user?.language]);

  const organisation = user?.organisation?.name;
  const weeks = pilot?.weeks;

  return (
    <main className='mx-auto flex min-h-dvh max-w-150 flex-col gap-6 px-6 py-12'>
      <div>
        <Wordmark registered className='h-8' />
      </div>

      <Card>
        <h1 className='text-xl font-semibold tracking-tight'>{m.pilot_ended_title()}</h1>

        <p className='mt-3 text-base text-ink-muted'>
          {weeks && organisation
            ? m.pilot_ended_body({ weeks, organisation })
            : m.pilot_ended_body_short()}
        </p>

        <p className='mt-3 text-base text-ink-muted'>{m.pilot_ended_contact_body()}</p>

        <a
          href='mailto:hello@wundo.ai'
          className={cn(
            'mt-6',
            buttonVariants({ variant: 'accent', shape: 'block', size: 'lg' }),
          )}
        >
          {m.pilot_ended_contact_action()}
        </a>

        <p className='mt-3 text-sm text-ink-muted'>{m.pilot_ended_contact_hint()}</p>
      </Card>

      <div>
        <Link to='/logout' className={buttonVariants({ variant: 'link', size: 'sm' })}>
          {m.pilot_ended_sign_out()}
        </Link>
      </div>
    </main>
  );
}
